import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Layout } from "@/components/Layout";
import { useLanguage } from "@/context/LanguageContext";
import { BookOpen, Clock, HelpCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ExamList = () => {
  const { t } = useLanguage();
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchExams();
  }, []);

  const fetchExams = async () => {
    try {
      const response = await axios.get(`${API}/exams`);
      setExams(response.data);
    } catch (error) {
      console.error("Error fetching exams:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="p-6 md:p-12" data-testid="exam-list-page">
        {/* Header */}
        <div className="mb-12">
          <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2">
            {t("exams")}
          </p>
          <h1 className="font-serif text-3xl md:text-4xl">
            {t("practice_exams")}
          </h1>
        </div>

        {/* Exams Grid */}
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-48 loading-skeleton" />
            ))}
          </div>
        ) : exams.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {exams.map((exam) => (
              <Link
                key={exam.id}
                to={`/exams/${exam.id}`}
                className="exam-card"
                data-testid={`exam-card-${exam.id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 border flex items-center justify-center">
                    <BookOpen size={24} strokeWidth={1} />
                  </div>
                </div>

                <h3 className="font-serif text-xl mb-2">{exam.title}</h3>
                
                {exam.description && (
                  <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                    {exam.description}
                  </p>
                )}

                <div className="flex items-center gap-4 text-xs font-mono text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <HelpCircle size={14} />
                    {exam.question_count} {t("questions")}
                  </span>
                  {exam.time_limit_minutes && (
                    <span className="flex items-center gap-1">
                      <Clock size={14} />
                      {exam.time_limit_minutes} {t("minutes")}
                    </span>
                  )}
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <BookOpen className="empty-state-icon" />
            <p className="empty-state-title">{t("no_exams_available")}</p>
            <p className="empty-state-description">
              {t("no_exams_empty_message")}
            </p>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ExamList;
